import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, MOTION, useGSAP } from '@/lib/motion'

interface MagneticProps {
    children: ReactNode
    className?: string
    strength?: number
}

/** Pulls its content gently toward the pointer on precise pointers. */
export const Magnetic: FC<MagneticProps> = ({ children, className, strength = 0.3 }) => {
    const ref = useRef<HTMLSpanElement>(null)

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const mm = gsap.matchMedia()

        mm.add(`${MOTION.ok} and ${MOTION.fine}`, () => {
            const toX = gsap.quickTo(element, 'x', { duration: 0.6, ease: 'power3.out' })
            const toY = gsap.quickTo(element, 'y', { duration: 0.6, ease: 'power3.out' })

            const onMove = (event: PointerEvent) => {
                const rect = element.getBoundingClientRect()

                toX((event.clientX - (rect.left + rect.width / 2)) * strength)
                toY((event.clientY - (rect.top + rect.height / 2)) * strength)
            }

            const onLeave = () => {
                toX(0)
                toY(0)
            }

            element.addEventListener('pointermove', onMove)
            element.addEventListener('pointerleave', onLeave)

            return () => {
                element.removeEventListener('pointermove', onMove)
                element.removeEventListener('pointerleave', onLeave)
                gsap.set(element, { x: 0, y: 0 })
            }
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <span ref={ref} className={cn('inline-block will-change-transform', className)}>
            {children}
        </span>
    )
}
